/**
 * Argus Live — short-lived signed iframe token hook (R4 P0).
 *
 * ## Why this exists
 *
 * The Argus Live editor preview pane mounts an `<iframe sandbox="...">`
 * pointed at `/api/v1/argus-live/dashboards/:id/iframe`. Because the
 * canonical sandbox attribute is `'allow-scripts allow-popups
 * allow-popups-to-escape-sandbox'` (no `allow-same-origin`) the iframe
 * is null-origin and CANNOT read the parent SPA's `localStorage`
 * Bearer token. Result: every iframe load returned 401 MISSING_TOKEN
 * and the preview pane stayed blank.
 *
 * The R4 P0 fix introduces a `POST /argus-live/dashboards/:id/iframe-token`
 * endpoint that mints a 5-minute signed JWT scoped to one
 * (dashboard, version) pair. The SPA fetches the token, sets the
 * iframe `src` with `?token=<jwt>`, and the iframe-content route
 * accepts it as a Bearer-equivalent.
 *
 * This hook owns the entire lifecycle:
 *   1. Mint the token via `apiClient.post(...)` (Bearer-authed).
 *   2. Build the iframe `src` with `?token=<jwt>` appended.
 *   3. Schedule a refresh 30s before expiry so the preview stays
 *      rendered during long editing sessions.
 *   4. Refetch when the (dashboardId, versionId) tuple changes.
 *   5. Cancel pending timers on unmount.
 *
 * ## Why a refresh-30s-early
 *
 * The token TTL is 5 minutes (server-side constant
 * `IFRAME_TOKEN_TTL_SECONDS`). Without proactive refresh, the iframe
 * would 401 the moment the token expired and the user would see a
 * blank pane until they reloaded the page. Refresh 30s early gives
 * us a sane buffer for clock skew + network latency.
 *
 * ## Failure modes
 *
 * If `apiClient.post` returns an error (403 because the user lost
 * their grant, 404 because the version was deleted, 5xx because the
 * API is unhealthy), `iframeSrc` becomes `null` and the editor falls
 * through to the empty-state rendering. We deliberately do NOT
 * surface the raw error to the iframe — the user already sees a
 * usable "preview unavailable" state in the editor wrapper.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from './api-client';

/** Refresh the token this many ms before its absolute expiry. */
const TOKEN_REFRESH_GUARD_MS = 30_000;

interface IframeTokenResponse {
  token: string;
  expiresAt: string;
}

interface UseArgusLiveIframeTokenResult {
  /**
   * Final iframe `src` URL with token appended. `null` when the token
   * hasn't been minted yet, the inputs are incomplete, or the token
   * fetch failed.
   */
  iframeSrc: string | null;
  /** Error message from the last failed mint attempt; null otherwise. */
  error: string | null;
  /** True while a token fetch is in flight (initial load + refresh). */
  isLoading: boolean;
}

/**
 * Build the iframe URL with the signed token query string appended.
 * The `_r` cache-busting nonce is appended LAST so it doesn't change
 * the canonical token-bound URL — the server ignores `_r` but the
 * browser uses it to invalidate any cache between saves.
 *
 * Exported for unit tests (the integration tests need to assert the
 * shape independently of the React lifecycle).
 */
export function buildIframeSrcWithToken(
  dashboardId: string,
  versionId: string,
  token: string,
  refreshNonce: number,
): string {
  const qs = new URLSearchParams({
    versionId,
    token,
  });
  // Cache-bust on save — keep `_r` last so the token URL is stable
  // across saves at parity.
  return `/api/v1/argus-live/dashboards/${dashboardId}/iframe?${qs.toString()}&_r=${refreshNonce}`;
}

/**
 * Fetch + maintain a fresh iframe token for the given (dashboard,
 * version) tuple. Returns the iframe `src` URL ready to assign to
 * `<iframe src=...>`.
 *
 * `refreshNonce` is forwarded to the URL as `&_r=<n>` so the parent
 * can force-reload the iframe after a save without re-minting the
 * token (we'd typically just bump the nonce and React will re-set
 * the same `src` value, triggering a navigation in the iframe).
 */
export function useArgusLiveIframeToken(input: {
  dashboardId: string;
  versionId: string | null;
  refreshNonce: number;
  /**
   * When true, skip token minting entirely (caller doesn't need a
   * preview, e.g. on an archived dashboard with no permission to
   * view). The hook returns `iframeSrc: null` without firing any
   * network calls.
   */
  disabled?: boolean;
}): UseArgusLiveIframeTokenResult {
  const { dashboardId, versionId, refreshNonce, disabled } = input;

  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Refresh-timer handle — cleared on every effect re-run + unmount.
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Cancel any pending refresh from a prior (dashboard, version) pair.
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (disabled || !versionId) {
      setToken(null);
      setExpiresAt(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchToken = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await apiClient.post<IframeTokenResponse>(
          `/argus-live/dashboards/${dashboardId}/iframe-token`,
          { dashboardVersionId: versionId },
        );
        if (cancelled) return;
        // `apiClient.post` returns parsed JSON directly; the server
        // wraps the response as { token, expiresAt }.
        const body = (resp as unknown as IframeTokenResponse | { data?: IframeTokenResponse });
        // Tolerate either { token, expiresAt } or { data: { token, expiresAt } }
        // — depending on whether a future API change adopts the
        // common `{ data: ... }` envelope, this hook keeps working.
        const out =
          'token' in body && typeof body.token === 'string'
            ? (body as IframeTokenResponse)
            : (body as { data: IframeTokenResponse }).data;
        if (!out || typeof out.token !== 'string') {
          throw new Error('Malformed token response');
        }
        setToken(out.token);
        const expiryMs = new Date(out.expiresAt).getTime();
        if (Number.isFinite(expiryMs)) {
          setExpiresAt(expiryMs);
          // Schedule a refresh 30s before expiry. If the TTL is
          // accidentally < 30s, fire immediately.
          const refreshInMs = Math.max(0, expiryMs - Date.now() - TOKEN_REFRESH_GUARD_MS);
          refreshTimerRef.current = setTimeout(() => {
            if (!cancelled) void fetchToken();
          }, refreshInMs);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setToken(null);
        setExpiresAt(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void fetchToken();

    return () => {
      cancelled = true;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
    // Intentionally NOT depending on `expiresAt` — refresh is scheduled
    // inside fetchToken's `setTimeout` and re-runs in the same effect
    // closure; depending on it would create an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, versionId, disabled]);

  const iframeSrc = useMemo(() => {
    if (!versionId || !token) return null;
    return buildIframeSrcWithToken(dashboardId, versionId, token, refreshNonce);
  }, [dashboardId, versionId, token, refreshNonce]);

  // ─── Suppress unused-var lint on expiresAt — it's part of the state
  // machine but not exposed to callers. Surface to React DevTools by
  // keeping it in state vs a ref so the hook is debuggable.
  void expiresAt;

  return {
    iframeSrc,
    error,
    isLoading,
  };
}
